/**
 * UserInsights — Pillar III · Identity (User Analytics).
 *
 * Phase-5 rewrite of the User Analytics page into the editorial Posture-pillar
 * pattern. Replaces the legacy single-page stack of stats / alerts / tables
 * with a tabbed editorial layout:
 *
 *   1. Standfirst    — kicker, italic-mixed headline, deck, byline + ScoreBox
 *      (driven by useIdentityBrief; score = MFA member adoption %)
 *   2. PillarTabs    — Overview / MFA / Licenses / Inactive / All Users
 *      (URL-synced via ?tab=…; default = Overview elides from URL)
 *   3. Per-tab body  — Overview is the editorial composition (KPI strip, MFA
 *      donut, License utilization, 90d trend, "People needing a hand" list).
 *      MFA / Licenses / Inactive / All Users wrap the existing
 *      <UserIssuesTable> with the right pre-selected filter.
 *   4. FooterRail    — cross-pillar nav (Pillar III of III)
 *
 * Data wiring (NO new endpoints — every value is already on the wire):
 *   - getUserInsights              → summary, KPIs, license waste user count
 *   - getUserInsightsLicenses      → per-SKU stacked bars (used / unused / available)
 *   - getUserInsightsUsers         → "People needing a hand" + tab tables
 *   - getUserInsightsStatus        → in-progress polling + last-refreshed
 *   - getPhishResistantMfaSummary  → 3-slice MFA donut
 *   - getUserInsightsTrends        → 90d adoption chart (existing component)
 *   - getUserInsightsSettings      → inactivity threshold (header dropdown)
 *
 * Voice contract (CLAUDE.md §Product Voice):
 *   - Verdicts ≤ 6 words, lead with named subjects
 *   - Editorial typography (italic dropcaps, double-rule section breaks)
 *   - License waste is ALWAYS rendered as a USER COUNT — never $/mo
 *
 * §2.3 deferred items resolved here:
 *   - Score-box composite breakdown → real Guardian Score components, not invented Identity weights
 *   - "Authenticator app" donut label → "Standard MFA"
 *   - $3,420/month recoverable → dropped (no SKU pricing model)
 *   - License utilization per-SKU dormant/disabled → tenant-level summary line only
 *   - "Rollout of FIDO2" annotation → dropped (no event store)
 *   - "Global Admin" role tag → jobTitle (real)
 *   - "142 sign-ins / 30d" → "Last sign-in Nd ago" relative-time
 *   - "Send setup link →" CTA → "Open user →" deep-link (no write API)
 *   - "Reclaim license →" CTA → "Open user →" (no one-click reassign)
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon,
  UsersIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useIdentityBrief } from '@/hooks/useIdentityBrief'
import {
  calculateStarredBpaScore,
  useComplianceScores,
} from '@/hooks/useComplianceData'
import type { UserIssueFilter, UserDetailItem } from '@/types'
import { DEFAULT_INACTIVITY_THRESHOLD_DAYS } from '@/types'

import LicenseUtilization from '@/components/UserInsights/LicenseUtilization'
import UserInsightsTrends from '@/components/UserInsights/UserInsightsTrends'
import UserIssuesTable from '@/components/UserInsights/UserIssuesTable'
import GlobalAdminMfaCallout from '@/components/UserInsights/GlobalAdminMfaCallout'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, {
  usePillarTab,
  type TabConfig,
} from '@/components/PostureBrief/PillarTabs'
import KpiStrip, { type KpiCell } from '@/components/PostureBrief/KpiStrip'
import FooterRail from '@/components/PostureBrief/FooterRail'

const THRESHOLD_OPTIONS = [30, 60, 90, 120, 180, 365] as const

/** Tab IDs for the User Analytics page. URL-synced via PillarTabs. */
type UserAnalyticsTab = 'overview' | 'mfa' | 'licenses' | 'inactive' | 'all'
const TAB_IDS: ReadonlyArray<UserAnalyticsTab> = [
  'overview',
  'mfa',
  'licenses',
  'inactive',
  'all',
]

/** Each tab pre-selects the filter that drives the embedded UserIssuesTable.
 *  Overview is "All" because it doesn't render the table — the filter only
 *  matters when the user navigates over to a tab that does. */
const TAB_FILTER: Record<UserAnalyticsTab, UserIssueFilter> = {
  overview: 'All',
  mfa: 'NoMfa',
  licenses: 'LicenseWaste',
  inactive: 'InactiveMembers',
  all: 'All',
}

export default function UserInsights() {
  const {
    defaultTenantId,
    isLoading: contextLoading,
    userTenants,
    isGlobalAdmin,
  } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(
    undefined,
  )

  // The active editorial tab. Driven by PillarTabs hook so URL stays in sync.
  const [activeTab] = usePillarTab(TAB_IDS, 'overview')

  // Scope param — narrows the user-issues table to a named cohort. Today
  // only `globalAdmins` is honored (other values are reserved for future
  // cohorts). The Operations panel emits identity alerts that link here with
  // `?scope=globalAdmins` to honor "1 GA without MFA" → land on the GA, not
  // the entire no-MFA list. Composes ON TOP of the active tab's base filter.
  const [searchParams, setSearchParams] = useSearchParams()
  const rawScope = searchParams.get('scope')
  const scopeGlobalAdmins = rawScope === 'globalAdmins'

  /** Remove only the `scope` param while keeping `tab` and any others.
   *  Used by the "Clear" affordance on the GA filter banner. */
  const clearScope = () => {
    setSearchParams(
      prev => {
        prev.delete('scope')
        return prev
      },
      { replace: true },
    )
  }

  // The filter state for the user-issues table. We keep it as page-level state
  // so the user can switch chips inside the All-Users tab without re-reading
  // the URL — but each tab change auto-resets it via the effect below.
  const [selectedFilter, setSelectedFilter] = useState<UserIssueFilter>('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [trendDays, setTrendDays] = useState(90)
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // When the user switches editorial tabs, snap the underlying user-table
  // filter to the tab-bound default. Inside All-Users tab the user is then
  // free to flip chips; the next tab change resets it again.
  useEffect(() => {
    setSelectedFilter(TAB_FILTER[activeTab])
    setCurrentPage(1)
  }, [activeTab])

  // Set default tenant when context loads. Same logic the legacy page used so
  // empty / fallback states behave identically.
  useEffect(() => {
    if (!contextLoading) {
      if (isGlobalAdmin) {
        if (defaultTenantId && defaultTenantId !== undefined) {
          setSelectedTenantId(defaultTenantId)
        }
      } else {
        if (defaultTenantId !== undefined) {
          setSelectedTenantId(defaultTenantId)
        } else if (userTenants.length > 0) {
          setSelectedTenantId(userTenants[0].tenantId)
        }
      }
    }
  }, [contextLoading, defaultTenantId, isGlobalAdmin, userTenants])

  // Close settings dropdown when clicking outside.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node)
      ) {
        setShowSettingsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ---- Data queries (same query keys + cadence as legacy page so the cache
  //      stays warm when navigating between Posture pages) ----

  const {
    data: insights,
    isLoading: insightsLoading,
    error: insightsError,
    refetch: refetchInsights,
  } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getUserInsights(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: insightsSettings } = useQuery({
    queryKey: ['user-insights-settings', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsSettings(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
  })

  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['user-insights-licenses', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsLicenses(selectedTenantId)
        : Promise.resolve([]),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: usersData, isLoading: usersLoading } = useQuery({
    // `scopeGlobalAdmins` is part of the cache key so React Query holds
    // independent caches for scoped vs unscoped views. Without this, the
    // cached unscoped page would be served when the scope flips on, briefly
    // misrepresenting the cohort before refetch.
    queryKey: [
      'user-insights-users',
      selectedTenantId,
      selectedFilter,
      currentPage,
      scopeGlobalAdmins,
    ],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsUsers(
            selectedTenantId,
            selectedFilter,
            currentPage,
            20,
            scopeGlobalAdmins,
          )
        : Promise.resolve({
            items: [],
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
          }),
    enabled: !!selectedTenantId,
  })

  const { data: refreshStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['user-insights-status', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsStatus(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    // Faster cadence so the in-progress pill clears quickly after a scan ends.
    refetchInterval: 10000,
  })

  const { data: phishResistantMfa } = useQuery({
    queryKey: ['userInsights', 'phishResistantMfa', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getPhishResistantMfaSummary(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['userInsights', 'trends', selectedTenantId, trendDays],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsTrends(selectedTenantId, trendDays)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Top-6 users for "People needing a hand". Limit=6 keeps the list editorial;
  // page=1 is fine because we sort server-side by issueType priority.
  const { data: noMfaUsers, isLoading: noMfaUsersLoading } = useQuery({
    queryKey: ['userInsights', 'people-needing-hand', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsUsers(selectedTenantId, 'NeedsAttention', 1, 6)
        : Promise.resolve({
            items: [],
            total: 0,
            page: 1,
            limit: 6,
            totalPages: 0,
          }),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Score components for the standfirst score box. These are the SAME four
  // Guardian-Score components the Command Center shows — NOT invented
  // Identity-pillar weights. Per §2.3, this keeps the visual identical while
  // sourcing every value from real data.
  const { data: secureScoreSummary } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: dashboardStats } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () => api.getDashboardStats(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores } = useComplianceScores(selectedTenantId)

  // Mirrors Dashboard.tsx — uses `weightedScore` so the composite-bar tile
  // matches the BPA pillar's hero metric (which sources `weightedScore` via
  // `transformToDisplayFrameworks`).
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  // ---- Mutations (preserved exactly from legacy page) ----

  const refreshMutation = useMutation({
    mutationFn: () => api.refreshUserInsights(selectedTenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user-insights-status', selectedTenantId],
      })
    },
  })

  const updateThresholdMutation = useMutation({
    mutationFn: (newThreshold: number) =>
      api.updateUserInsightsSettings(selectedTenantId!, {
        inactivityThresholdDays: newThreshold,
      }),
    onSuccess: (_data, newThreshold) => {
      queryClient.invalidateQueries({
        queryKey: ['user-insights-settings', selectedTenantId],
      })
      queryClient.invalidateQueries({ queryKey: ['user-insights', selectedTenantId] })
      queryClient.invalidateQueries({
        queryKey: ['user-insights-users', selectedTenantId],
      })
      toast.success(
        `Inactivity threshold updated to ${newThreshold} days. A new scan is needed to recalculate user data.`,
      )
      setShowSettingsDropdown(false)
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to update settings'
      toast.error(errorMessage)
    },
  })

  const handleRefresh = () => {
    if (selectedTenantId) refreshMutation.mutate()
  }

  // ---- Derived view-model ----

  const currentThreshold =
    insightsSettings?.inactivityThresholdDays ??
    insights?.inactivityThresholdDays ??
    DEFAULT_INACTIVITY_THRESHOLD_DAYS

  const isNonDefaultThreshold = currentThreshold !== DEFAULT_INACTIVITY_THRESHOLD_DAYS

  // GA-scope narrowing. When the URL carries `?scope=globalAdmins` (emitted
  // by the Operations panel's identity alerts: GA-no-MFA + dormant-GA), the
  // user-issues query passes `globalAdminsOnly=true` to the server so the
  // result set is narrowed AND paginated correctly under the active filter.
  // Composes ON TOP of the tab's base filter (e.g. tab=mfa + scope honored
  // → only GAs without MFA, paged authoritatively).
  //
  // The previous client-side filter on `usersData.items` only narrowed the
  // current page of 20 rows and broke for tenants with >20 GAs. Server-side
  // is now the contract.

  // Filter counts for the All-Users tab chip row + table sub-pills. Same map
  // the legacy page computed; only the rendering host changes.
  const filterCounts = useMemo(() => {
    if (!insights) return undefined
    const mfaEnabled = insights.mfaEnabledMembers ?? 0
    return {
      All: insights.totalUsers ?? 0,
      ActiveMembers: insights.activeMemberUsers ?? 0,
      ActiveGuests: insights.activeGuestUsers ?? 0,
      NeedsAttention: insights.needsAttentionUsers ?? 0,
      NoMfa: insights.noMfaEnabledUsers ?? 0,
      NoPhishResistantMfa: mfaEnabled - (insights.phishResistantMfaUsers ?? 0),
      HasPhishResistantMfa: insights.phishResistantMfaUsers ?? 0,
      LicenseWaste: insights.totalLicenseWaste ?? 0,
      DisabledWithLicenses: insights.disabledUsersWithLicenses ?? 0,
      InactiveWithLicenses: insights.inactiveUsersWithLicenses ?? 0,
      InactiveMembers: insights.inactiveMemberUsers ?? 0,
      InactiveGuests: insights.inactiveGuestUsers ?? 0,
    } as Record<string, number>
  }, [insights])

  // The standfirst's score is the live MFA-member-adoption percent — same
  // number rendered on the Command Center triptych's PillarIdentity. Keeping
  // it stable across surfaces is intentional.
  const identityScore = insights?.mfaMemberAdoptionPercentage ?? null

  // Top action for the brief composer. Mirrors PillarIdentity's logic:
  // crit if anyone is missing MFA, then dormant cleanup, then license waste.
  const topAction = useMemo(() => {
    if (!insights) return undefined
    const noMfa = insights.noMfaEnabledUsers ?? 0
    const dormant = insights.inactiveMemberUsers ?? 0
    const waste = insights.totalLicenseWaste ?? 0
    if (noMfa > 0) {
      return {
        pillar: 'identity' as const,
        title: `Enroll ${noMfa} ${noMfa === 1 ? 'member' : 'members'} on MFA`,
        impact: 'crit' as const,
      }
    }
    if (dormant > 0) {
      return {
        pillar: 'identity' as const,
        title: `Review ${dormant} dormant ${dormant === 1 ? 'account' : 'accounts'}`,
        impact: 'high' as const,
      }
    }
    if (waste > 0) {
      return {
        pillar: 'identity' as const,
        title: `Reclaim ${waste} ${waste === 1 ? 'license' : 'licenses'}`,
        impact: 'med' as const,
      }
    }
    return undefined
  }, [insights])

  const brief = useIdentityBrief({
    tenantId: selectedTenantId,
    score: identityScore,
    // 30d adoption delta from trends, when we have it. The history endpoint
    // surfaces dated points, so we diff first vs last to get the period delta.
    deltas: (() => {
      const history = trendsData?.history
      if (!history || history.length < 2) return undefined
      const first = history[0]?.mfaMemberAdoptionPercentage
      const last = history[history.length - 1]?.mfaMemberAdoptionPercentage
      if (typeof first !== 'number' || typeof last !== 'number') return undefined
      return { score: last - first, days: trendDays }
    })(),
    topAction,
    criticalCount: insights?.noMfaEnabledUsers ?? 0,
  })

  // Guardian Score component bars — see top-of-file note on why these are
  // the real four components and not Identity-specific weights.
  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline drift',
        weight: '20%',
        value: Math.round(dashboardStats?.complianceScore?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
      {
        label: 'Best practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-secondaryBlue-300',
      },
      {
        label: 'MFA adoption',
        weight: '20%',
        value: Math.round(insights?.mfaMemberAdoptionPercentage ?? 0),
        fillClass: 'bg-secondarySand-300',
      },
      {
        label: 'M365 Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
    ],
    [
      dashboardStats?.complianceScore?.percentage,
      bpaStarredScore,
      insights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  const tabs: TabConfig<UserAnalyticsTab>[] = [
    { id: 'overview', label: 'Overview' },
    {
      id: 'mfa',
      label: 'MFA',
      badge: insights?.noMfaEnabledUsers ?? null,
    },
    {
      id: 'licenses',
      label: 'Licenses',
      // §2.3 explicit: totalLicenseWaste is a USER count, not a SKU count.
      // The badge mirrors that.
      badge: insights?.totalLicenseWaste ?? null,
    },
    {
      id: 'inactive',
      label: 'Inactive',
      badge: insights?.inactiveMemberUsers ?? null,
    },
    { id: 'all', label: 'All Users' },
  ]

  // ---- Early returns (loading / empty / error) ----

  if (contextLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12 text-sm text-slate-500">
          Loading tenant information…
        </div>
      </div>
    )
  }

  if (!selectedTenantId && isGlobalAdmin && userTenants.length > 0) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card p-12 text-center">
          <UsersIcon className="mx-auto h-16 w-16 text-slate-500 mb-4" />
          <h3 className="card-title text-lg mb-2 justify-center">
            Select a Tenant
          </h3>
          <p className="text-slate-400">
            Use the tenant switcher in the sidebar to choose a tenant for user
            analytics.
          </p>
        </div>
      </div>
    )
  }

  if (!selectedTenantId && userTenants.length === 0) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12 text-sm text-slate-500">
          No tenants available. Please contact your administrator.
        </div>
      </div>
    )
  }

  if (insightsError) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-red-400 mb-4">Failed to load user insights data</div>
          <button onClick={() => refetchInsights()} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    )
  }

  // ---- Main render ----

  return (
    <div className="mx-auto max-w-[1320px]">
      <PillarBrief
        brief={brief}
        score={identityScore}
        components={compositeBars}
        tone="identity"
        isLoading={insightsLoading}
        scoreLabel="MFA Adoption %"
        // Surface the freshness of the underlying scan in the byline row so
        // the user can see when User Analytics data was last refreshed
        // without needing to drill into the scan-status panel. Source of
        // truth: `getUserInsightsStatus().lastRefreshedAt` — already polled
        // every 10s above.
        //
        // While the status query is in flight (`refreshStatus === undefined`)
        // we pass `undefined` so the segment is suppressed entirely — the
        // alternative would briefly flash "Awaiting first sync." before the
        // real timestamp lands. Once the query resolves, an actual
        // `lastRefreshedAt` renders as "Updated N ago", and a missing one
        // resolves to the graceful "Awaiting first sync." copy.
        lastRefreshedAt={
          refreshStatus ? refreshStatus.lastRefreshedAt ?? '' : undefined
        }
      />

      <PillarTabs
        tabs={tabs}
        defaultTab="overview"
        tone="identity"
        ariaLabel="User Analytics view"
        actions={
          <TabActions
            settingsRef={settingsRef}
            currentThreshold={currentThreshold}
            showSettingsDropdown={showSettingsDropdown}
            onToggleSettings={() => setShowSettingsDropdown(v => !v)}
            onUpdateThreshold={(days) => updateThresholdMutation.mutate(days)}
            isUpdatingThreshold={updateThresholdMutation.isPending}
            onStartScan={handleRefresh}
            isStartingScan={refreshMutation.isPending}
            tenantSelected={!!selectedTenantId}
          />
        }
      />

      {/* Scan-in-progress / non-default threshold / no-data notices live ABOVE
          the per-tab body so they are visible regardless of the active tab. */}
      <NoticeBlock
        refreshStatus={refreshStatus}
        statusLoading={statusLoading}
        insights={insights}
        isNonDefaultThreshold={isNonDefaultThreshold}
        currentThreshold={currentThreshold}
      />

      {activeTab === 'overview' && (
        <>
          {/* GA-MFA operational-risk callout. Renders above the Overview body
              when `noMfaGlobalAdmins > 0`, returns null otherwise. The general
              identity brief above keeps its all-users framing; this callout
              focuses on the highest-impact MFA gap by name (per CLAUDE.md
              §Product Voice). */}
          <GlobalAdminMfaCallout
            count={insights?.noMfaGlobalAdmins ?? 0}
          />
          <OverviewTab
            insights={insights}
            phishResistantMfa={phishResistantMfa}
            licenses={licenses ?? []}
            licensesLoading={licensesLoading}
            trendsData={trendsData ?? null}
            trendsLoading={trendsLoading}
            trendDays={trendDays}
            onTrendDaysChange={setTrendDays}
            peopleNeedingHand={noMfaUsers?.items ?? []}
            peopleNeedingHandLoading={noMfaUsersLoading}
            currentThreshold={currentThreshold}
          />
        </>
      )}

      {activeTab === 'mfa' && (
        <>
          <GlobalAdminScopeBanner
            active={scopeGlobalAdmins}
            onClear={clearScope}
            visibleCount={usersData?.items.length ?? 0}
            total={usersData?.total ?? 0}
          />
          <UserIssuesTable
            users={usersData?.items ?? []}
            total={usersData?.total ?? 0}
            currentPage={currentPage}
            totalPages={usersData?.totalPages || 0}
            selectedFilter={selectedFilter}
            onFilterChange={(filter) => {
              setSelectedFilter(filter)
              setCurrentPage(1)
            }}
            onPageChange={setCurrentPage}
            isLoading={usersLoading}
            inactivityThresholdDays={currentThreshold}
            tenantId={selectedTenantId}
            filterCounts={filterCounts}
            resourceAccountCount={insights?.resourceAccountCount}
            globalAdminsOnly={scopeGlobalAdmins}
          />
        </>
      )}

      {activeTab === 'licenses' && (
        <div className="space-y-8">
          <LicenseUtilization
            licenses={licenses ?? []}
            insights={insights}
            isLoading={licensesLoading}
          />
          <GlobalAdminScopeBanner
            active={scopeGlobalAdmins}
            onClear={clearScope}
            visibleCount={usersData?.items.length ?? 0}
            total={usersData?.total ?? 0}
          />
          <UserIssuesTable
            users={usersData?.items ?? []}
            total={usersData?.total ?? 0}
            currentPage={currentPage}
            totalPages={usersData?.totalPages || 0}
            selectedFilter={selectedFilter}
            onFilterChange={(filter) => {
              setSelectedFilter(filter)
              setCurrentPage(1)
            }}
            onPageChange={setCurrentPage}
            isLoading={usersLoading}
            inactivityThresholdDays={currentThreshold}
            tenantId={selectedTenantId}
            filterCounts={filterCounts}
            resourceAccountCount={insights?.resourceAccountCount}
            globalAdminsOnly={scopeGlobalAdmins}
          />
        </div>
      )}

      {activeTab === 'inactive' && (
        <>
          <GlobalAdminScopeBanner
            active={scopeGlobalAdmins}
            onClear={clearScope}
            visibleCount={usersData?.items.length ?? 0}
            total={usersData?.total ?? 0}
          />
          <UserIssuesTable
            users={usersData?.items ?? []}
            total={usersData?.total ?? 0}
            currentPage={currentPage}
            totalPages={usersData?.totalPages || 0}
            selectedFilter={selectedFilter}
            onFilterChange={(filter) => {
              setSelectedFilter(filter)
              setCurrentPage(1)
            }}
            onPageChange={setCurrentPage}
            isLoading={usersLoading}
            inactivityThresholdDays={currentThreshold}
            tenantId={selectedTenantId}
            filterCounts={filterCounts}
            resourceAccountCount={insights?.resourceAccountCount}
            globalAdminsOnly={scopeGlobalAdmins}
          />
        </>
      )}

      {activeTab === 'all' && (
        <>
          <GlobalAdminScopeBanner
            active={scopeGlobalAdmins}
            onClear={clearScope}
            visibleCount={usersData?.items.length ?? 0}
            total={usersData?.total ?? 0}
          />
          <UserIssuesTable
            users={usersData?.items ?? []}
            total={usersData?.total ?? 0}
            currentPage={currentPage}
            totalPages={usersData?.totalPages || 0}
            selectedFilter={selectedFilter}
            onFilterChange={(filter) => {
              setSelectedFilter(filter)
              setCurrentPage(1)
            }}
            onPageChange={setCurrentPage}
            isLoading={usersLoading}
            inactivityThresholdDays={currentThreshold}
            tenantId={selectedTenantId}
            filterCounts={filterCounts}
            resourceAccountCount={insights?.resourceAccountCount}
            globalAdminsOnly={scopeGlobalAdmins}
          />
        </>
      )}

      <FooterRail currentTone="identity" />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Tab actions slot — inactivity threshold + Start new scan                   */
/* -------------------------------------------------------------------------- */

/** Inactivity-threshold dropdown + Start-new-scan button. Lives in the
 *  PillarTabs `actions` slot so the controls stay docked to the tab strip
 *  (same UX the legacy page-header had, just relocated). The dropdown's
 *  outside-click behavior is owned by the parent so we don't double-bind. */
function TabActions(props: {
  settingsRef: React.RefObject<HTMLDivElement>
  currentThreshold: number
  showSettingsDropdown: boolean
  onToggleSettings: () => void
  onUpdateThreshold: (days: number) => void
  isUpdatingThreshold: boolean
  onStartScan: () => void
  isStartingScan: boolean
  tenantSelected: boolean
}) {
  if (!props.tenantSelected) return null

  return (
    <>
      <div className="relative" ref={props.settingsRef}>
        <button
          onClick={props.onToggleSettings}
          className="btn-secondary inline-flex items-center justify-center h-9 px-3"
          title="Inactivity threshold settings"
        >
          <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
          {props.currentThreshold}d
        </button>

        {props.showSettingsDropdown && (
          <div className="absolute right-0 mt-2 w-64 bg-dark-800 border border-dark-600 rounded-xl shadow-xl z-50">
            <div className="px-4 py-3 border-b border-dark-700">
              <p className="text-sm font-medium text-slate-200">Inactivity Threshold</p>
              <p className="text-xs text-slate-400 mt-1">
                Users with no sign-in activity beyond this period are flagged as inactive.
              </p>
            </div>
            <div className="p-2">
              {THRESHOLD_OPTIONS.map((days) => (
                <button
                  key={days}
                  onClick={() => props.onUpdateThreshold(days)}
                  disabled={props.isUpdatingThreshold}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                    days === props.currentThreshold
                      ? 'bg-primary-500/20 text-primary-300 font-medium'
                      : 'text-slate-300 hover:bg-dark-700'
                  } disabled:opacity-50`}
                >
                  {days} days
                  {days === DEFAULT_INACTIVITY_THRESHOLD_DAYS && (
                    <span className="ml-2 text-xs text-slate-500">(default)</span>
                  )}
                  {days === props.currentThreshold && (
                    <span className="ml-2 text-xs text-primary-400">current</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={props.onStartScan}
        disabled={props.isStartingScan}
        className="inline-flex items-center justify-center h-9 px-3.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-glow-cyan hover:shadow-glow-cyan-lg hover:-translate-y-0.5 disabled:from-dark-600 disabled:to-dark-600 disabled:shadow-none disabled:cursor-not-allowed"
        title="Start a new scan to collect fresh data from Microsoft 365"
      >
        {props.isStartingScan ? (
          <>
            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            Start New Scan
          </>
        )}
      </button>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Notice block                                                               */
/* -------------------------------------------------------------------------- */

/** Inline notices that need to render regardless of active tab. Kept in their
 *  own component so the main render reads clean. */
function NoticeBlock(props: {
  refreshStatus: { status?: string; lastRefreshedAt?: string } | null | undefined
  statusLoading: boolean
  insights: { hasData?: boolean } | null | undefined
  isNonDefaultThreshold: boolean
  currentThreshold: number
}) {
  return (
    <>
      {props.refreshStatus?.status === 'InProgress' && (
        <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 border border-primary-500/30 rounded-full text-xs text-primary-400">
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>User analytics scan in progress…</span>
        </div>
      )}

      {props.isNonDefaultThreshold && (
        <div className="mb-6 bg-amber-500/10 light:bg-amber-50 border border-amber-500/20 light:border-amber-200 rounded-xl p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-amber-400 light:text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-300 light:text-amber-800">
                Custom Inactivity Threshold Active
              </h4>
              <p className="text-sm text-amber-400/80 light:text-amber-700 mt-1">
                Inactivity data is calculated using a{' '}
                <strong>{props.currentThreshold}-day</strong> threshold instead of the
                default {DEFAULT_INACTIVITY_THRESHOLD_DAYS} days. Users are flagged as
                inactive if they have had no sign-in activity for{' '}
                {props.currentThreshold}+ days.
              </p>
            </div>
          </div>
        </div>
      )}

      {!props.statusLoading && props.insights && !props.insights.hasData && (
        <div className="mb-6 bg-primary-500/10 border border-primary-500/20 rounded-xl p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-primary-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-primary-300">
                No User Analytics Data Available
              </h4>
              <p className="text-sm text-primary-400/80 mt-1">
                Click "Start New Scan" to collect user, license, and MFA information
                from Microsoft 365. This process typically takes 2-5 minutes depending
                on the number of users.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Global Admin scope banner                                                  */
/* -------------------------------------------------------------------------- */

/** Sticky chip rendered above the user-issues table when the page is scoped
 *  to Global Admins via `?scope=globalAdmins`. The Operations panel emits
 *  identity alerts ("Two Global Admins without MFA") that link to
 *  `/user-insights?tab=mfa&scope=globalAdmins` — this banner is the visible
 *  contract that the page actually honored the scope, not just the tab.
 *
 *  Voice: short, named subjects, no em-dash. The Clear control removes only
 *  the `scope` param so the user keeps their current tab.
 *
 *  The server now applies the GA narrowing via `globalAdminsOnly=true`, so
 *  `total` is authoritative for the GA-scoped cohort under the active filter.
 *  The earlier "limited to current page" caveat is gone. */
function GlobalAdminScopeBanner({
  active,
  onClear,
  visibleCount,
  total,
}: {
  active: boolean
  onClear: () => void
  /** Number of GA rows on the current page. */
  visibleCount: number
  /** Authoritative server-side count of GA rows under the active filter. */
  total: number
}) {
  if (!active) return null

  // Period-then-clause framing per CLAUDE.md voice. No em-dash. ≤ 14 words.
  // When server total === visible count we have the whole cohort on one
  // page, so drop the "M total" trailing clause and keep it tight.
  const lead = 'Filtered to Global Admins.'
  const tail =
    total > visibleCount
      ? `${visibleCount} on this page · ${total} total.`
      : visibleCount === 1
        ? 'One on this page.'
        : `${visibleCount} on this page.`
  const headline = `${lead} ${tail}`

  return (
    <div
      data-tone="identity"
      className="
        mb-5 flex flex-wrap items-center justify-between gap-3
        rounded-2xl border border-dark-600 bg-dark-800 px-5 py-3
      "
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <ShieldCheckIcon
          className="h-5 w-5 flex-shrink-0"
          style={{ color: 'var(--tone-accent-deep)', marginTop: '2px' }}
          aria-hidden
        />
        <div>
          <p className="text-[13px] font-bold tracking-[-0.005em] text-slate-100 dark:text-slate-100">
            {headline}
          </p>
        </div>
      </div>
      <button
        onClick={onClear}
        className="
          inline-flex items-center gap-1.5 rounded-full border border-dark-600
          bg-dark-900 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em]
          text-slate-100 transition-colors hover:border-current
        "
        style={{ color: 'var(--tone-accent-deep)' }}
        type="button"
      >
        <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
        Clear
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview tab                                                               */
/* -------------------------------------------------------------------------- */

/** Number-pad helper: '8' → '08', '23' → '23'. Matches the editorial
 *  italic-dropcap style from the mockups. */
function pad2(n: number): string {
  if (n < 0) return '00'
  return n < 10 ? `0${n}` : String(n)
}

/** Compose the Overview tab body. Pure presentation given upstream data. */
function OverviewTab(props: {
  insights: import('@/types').UserInsightsSummary | null | undefined
  phishResistantMfa: import('@/types').PhishResistantMfaSummary | null | undefined
  licenses: import('@/types').LicenseSkuSummary[]
  licensesLoading: boolean
  trendsData: import('@/types').UserInsightsTrendsResult | null
  trendsLoading: boolean
  trendDays: number
  onTrendDaysChange: (days: number) => void
  peopleNeedingHand: UserDetailItem[]
  peopleNeedingHandLoading: boolean
  currentThreshold: number
}) {
  const insights = props.insights
  const phish = props.phishResistantMfa

  const noMfa = insights?.noMfaEnabledUsers ?? 0
  const licenseWasteUsers = insights?.totalLicenseWaste ?? 0
  const dormant = insights?.inactiveMemberUsers ?? 0
  const disabledLicensed = insights?.disabledUsersWithLicenses ?? 0
  const inactiveLicensed = insights?.inactiveUsersWithLicenses ?? 0

  // KPI cells. Per §5 the License-waste cell is a USER count and the sub-line
  // is the disabled+dormant breakdown — explicitly NEVER $/mo.
  const kpiCells: KpiCell[] = [
    {
      id: 'no-mfa',
      numeral: pad2(noMfa),
      label: 'No MFA · members',
      // TODO: surface admin-count once directory roles ingestion ships
      // (see .agents/Tasks/Not Started/posture-pages-supplements.md).
      stat:
        noMfa === 1
          ? '1 enabled member'
          : `${noMfa.toLocaleString()} enabled members`,
      severity: noMfa > 0 ? 'crit' : 'tone',
    },
    {
      id: 'license-waste',
      numeral: pad2(licenseWasteUsers),
      // §2.3: NOT "SKUs", NOT "$N/mo". User count.
      label: 'License waste · users',
      stat: `${disabledLicensed} disabled · ${inactiveLicensed} dormant`,
      severity: licenseWasteUsers > 0 ? 'warn' : 'tone',
    },
    {
      id: 'dormant',
      numeral: pad2(dormant),
      label: `Dormant · ${props.currentThreshold}+ days`,
      stat: 'No sign-in activity',
      severity: 'tone',
    },
  ]

  return (
    <div>
      <KpiStrip tone="identity" cells={kpiCells} />

      {/* MFA donut + License utilization two-up. Both sit on the editorial
          card surface; the bodies inside reuse existing components so chart
          theming stays in lock-step with the rest of the app. */}
      <section
        data-tone="identity"
        className="mb-9 grid grid-cols-1 gap-7 lg:grid-cols-2"
      >
        <MfaDonut
          phish={phish ?? null}
          totalEnabled={insights?.enabledMemberUsers ?? 0}
        />

        <LicenseUtilizationCard
          licenses={props.licenses}
          isLoading={props.licensesLoading}
          insights={insights}
        />
      </section>

      {/* 90d adoption trend — UserInsightsTrends now owns its own editorial
          card chrome (Section 03), so we just give it space in the column. */}
      <div className="mb-9">
        <UserInsightsTrends
          trends={props.trendsData}
          isLoading={props.trendsLoading}
          days={props.trendDays}
          onDaysChange={props.onTrendDaysChange}
        />
      </div>

      {/* People needing a hand — top 6 from the user-issues endpoint. */}
      <PeopleNeedingHand
        users={props.peopleNeedingHand}
        isLoading={props.peopleNeedingHandLoading}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview · MFA donut                                                       */
/* -------------------------------------------------------------------------- */

/** Three-slice MFA donut — Phish-resistant / Standard MFA / No MFA.
 *
 *  §2.3 explicitly relabels the middle bucket from "Authenticator app" to
 *  "Standard MFA" because it covers push, TOTP, SMS and voice — not just
 *  authenticator-app users.
 *
 *  Implemented as an inline SVG (matching the mockup) rather than a recharts
 *  pie because we need exact control over the dasharray offsets to render the
 *  three-segment ring with editorial color tuning. The legend uses the same
 *  hex pinning as the rest of the editorial palette so it stays consistent
 *  across light + dark modes. */
function MfaDonut({
  phish,
  totalEnabled,
}: {
  phish: import('@/types').PhishResistantMfaSummary | null
  totalEnabled: number
}) {
  const phishUsers = phish?.phishResistantMfaUsers ?? 0
  const standardUsers = phish?.standardMfaOnlyUsers ?? 0
  const noMfaUsers = phish?.noMfaUsers ?? 0

  const total = phishUsers + standardUsers + noMfaUsers
  const phishPct = total > 0 ? (phishUsers / total) * 100 : 0
  const standardPct = total > 0 ? (standardUsers / total) * 100 : 0
  const noMfaPct = total > 0 ? (noMfaUsers / total) * 100 : 0
  const onMfaPct =
    totalEnabled > 0
      ? Math.round(((phishUsers + standardUsers) / totalEnabled) * 100)
      : 0

  // Two admins fabrication is gone — we don't have role data. Sub-text just
  // states the count.
  const noMfaSubText =
    noMfaUsers === 1 ? '1 member' : `${noMfaUsers.toLocaleString()} members`

  return (
    <div className="rounded-3xl border border-dark-600 bg-dark-800 p-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Section · 01
      </div>
      <h3 className="mt-1 mb-5 text-[22px] font-extrabold tracking-[-0.015em] text-slate-100 dark:text-slate-100">
        MFA{' '}
        <em
          style={{ fontStyle: 'italic', fontWeight: 600, color: 'var(--tone-accent-deep)' }}
        >
          adoption
        </em>
      </h3>

      <div className="grid grid-cols-1 items-center gap-7 sm:grid-cols-[200px_1fr]">
        <div className="relative h-[200px] w-[200px] mx-auto sm:mx-0">
          <svg
            viewBox="0 0 36 36"
            className="h-full w-full"
            style={{ transform: 'rotate(-90deg)' }}
            aria-hidden
          >
            <circle
              cx="18"
              cy="18"
              r="15.9155"
              fill="transparent"
              stroke="rgb(var(--color-dark-700))"
              strokeWidth="3.6"
            />
            {phishPct > 0 && (
              <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="transparent"
                stroke="#76D69D"
                strokeWidth="3.6"
                strokeDasharray={`${phishPct} ${100 - phishPct}`}
                strokeDashoffset="0"
              />
            )}
            {standardPct > 0 && (
              <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="transparent"
                stroke="#A7D3F0"
                strokeWidth="3.6"
                strokeDasharray={`${standardPct} ${100 - standardPct}`}
                strokeDashoffset={`-${phishPct}`}
              />
            )}
            {noMfaPct > 0 && (
              <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="transparent"
                stroke="#E57373"
                strokeWidth="3.6"
                strokeDasharray={`${noMfaPct} ${100 - noMfaPct}`}
                strokeDashoffset={`-${phishPct + standardPct}`}
              />
            )}
          </svg>
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <div className="text-[44px] font-extrabold leading-none tracking-[-0.03em] text-slate-100 dark:text-slate-100">
                {onMfaPct}
                <span className="text-[14px] font-semibold text-slate-500 ml-0.5">
                  %
                </span>
              </div>
              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">
                On MFA
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          <DonutLegend
            color="#76D69D"
            name="Phish-resistant"
            sub="FIDO2 / Windows Hello"
            value={`${Math.round(phishPct)}%`}
          />
          <DonutLegend
            color="#A7D3F0"
            // Relabeled from "Authenticator app" — it covers all standard
            // MFA methods, not just authenticator-app users.
            name="Standard MFA"
            sub="Push / TOTP / SMS"
            value={`${Math.round(standardPct)}%`}
          />
          <DonutLegend
            color="#E57373"
            name="No MFA"
            sub={noMfaSubText}
            value={`${Math.round(noMfaPct)}%`}
          />
        </div>
      </div>
    </div>
  )
}

function DonutLegend({
  color,
  name,
  sub,
  value,
}: {
  color: string
  name: string
  sub: string
  value: string
}) {
  return (
    <div className="grid grid-cols-[14px_1fr_auto] items-center gap-3.5">
      <span
        aria-hidden
        className="block h-3.5 w-3.5 rounded-[4px]"
        style={{ background: color }}
      />
      <div>
        <div className="text-[13px] font-semibold text-slate-100 dark:text-slate-100">
          {name}
        </div>
        <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {sub}
        </div>
      </div>
      <div className="font-display text-[22px] font-extrabold italic tracking-[-0.02em] tabular-nums text-slate-100 dark:text-slate-100">
        {value}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview · License utilization card                                        */
/* -------------------------------------------------------------------------- */

/** License utilization with three-state per-SKU bars (used / unused /
 *  available) — §2.3 explicitly forbids per-SKU dormant/disabled because we
 *  only have those numbers at the tenant level. The tenant-level summary
 *  rolls up above the per-SKU bars. */
function LicenseUtilizationCard({
  licenses,
  isLoading,
  insights,
}: {
  licenses: import('@/types').LicenseSkuSummary[]
  isLoading: boolean
  insights: import('@/types').UserInsightsSummary | null | undefined
}) {
  // Filter out free / trial SKUs so the bars don't get visually dominated by
  // 0%-utilized free seats. Same heuristic the legacy LicenseUtilization
  // component uses, narrowed to a small inline check.
  const paid = licenses.filter(l => !isFreeOrTrialSku(l.skuPartNumber))

  // Sort by utilization desc, top 6 for editorial scan.
  const sorted = [...paid].sort(
    (a, b) => b.utilizationPercentage - a.utilizationPercentage,
  )
  const top = sorted.slice(0, 6)

  // Tenant-level aggregated counts that ARE available (per §2.3) — these
  // surface above the per-SKU bars so the disabled/dormant story is visible
  // at the right granularity.
  const disabledLicensed = insights?.disabledUsersWithLicenses ?? 0
  const inactiveLicensed = insights?.inactiveUsersWithLicenses ?? 0

  return (
    <div className="rounded-3xl border border-dark-600 bg-dark-800 p-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Section · 02
      </div>
      <h3 className="mt-1 mb-5 text-[22px] font-extrabold tracking-[-0.015em] text-slate-100 dark:text-slate-100">
        License{' '}
        <em
          style={{ fontStyle: 'italic', fontWeight: 600, color: 'var(--tone-accent-deep)' }}
        >
          utilization
        </em>
      </h3>

      {/* Tenant-level aggregated waste line — these counts ARE available even
          though the per-SKU breakdown is not. Always rendered so the user
          sees the disabled+licensed / inactive+licensed story up front. */}
      {(disabledLicensed > 0 || inactiveLicensed > 0) && (
        <p className="mb-5 text-[12px] leading-[1.55] text-slate-400">
          <strong className="text-slate-100 dark:text-slate-100">
            {disabledLicensed.toLocaleString()}
          </strong>{' '}
          disabled+licensed and{' '}
          <strong className="text-slate-100 dark:text-slate-100">
            {inactiveLicensed.toLocaleString()}
          </strong>{' '}
          dormant+licensed across all SKUs — candidates to reclaim.
        </p>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-dark-700" />
          ))}
        </div>
      ) : top.length === 0 ? (
        <p className="py-3 text-[13px] text-slate-400">
          No license SKUs to display. Run a scan to collect license data.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {top.map(sku => (
            <SkuRow key={sku.skuId} sku={sku} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Single SKU row: name + assigned/total meta + 3-state stacked bar.
 *  States: assigned (used) / unused (assigned but not yet active per the
 *  utilization model — currently equal to zero in the existing API, so we
 *  surface used + available as the binary fill) / available (unassigned). */
function SkuRow({ sku }: { sku: import('@/types').LicenseSkuSummary }) {
  const used = sku.usedLicenses
  const total = sku.totalLicenses
  const available = sku.availableLicenses
  const usedPct = total > 0 ? (used / total) * 100 : 0
  const availPct = total > 0 ? (available / total) * 100 : 0

  return (
    <div>
      <div className="mb-1.5 flex justify-between">
        <span className="text-[13px] font-bold text-slate-100 dark:text-slate-100">
          {sku.displayName || sku.skuPartNumber}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {used.toLocaleString()} / {total.toLocaleString()} assigned ·{' '}
          {sku.utilizationPercentage}%
        </span>
      </div>
      <div className="flex h-[22px] overflow-hidden rounded-lg border border-dark-600 bg-dark-900">
        {usedPct > 0 && (
          <div
            className="h-full"
            style={{ width: `${usedPct}%`, background: 'var(--tone-accent)' }}
            title={`${used} used`}
          />
        )}
        {availPct > 0 && (
          <div
            className="h-full"
            style={{ width: `${availPct}%`, background: 'rgb(var(--color-dark-700))' }}
            title={`${available} available`}
          />
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        <span>
          <span
            aria-hidden
            className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px]"
            style={{ background: 'var(--tone-accent)', verticalAlign: 'middle' }}
          />
          <strong className="text-slate-100 dark:text-slate-100">
            {used.toLocaleString()}
          </strong>{' '}
          used
        </span>
        <span>
          <strong className="text-slate-100 dark:text-slate-100">
            {available.toLocaleString()}
          </strong>{' '}
          available
        </span>
      </div>
    </div>
  )
}

const FREE_LICENSE_PATTERNS = [
  'FREE',
  'TRIAL',
  'VIRAL',
  'EXPLORATORY',
  'SEEDED',
  'DEVELOPERPACK',
  '_DEV',
  'VIRTUALUSER',
  '_ADHOC',
] as const

/** Mirrors `isFreeOrTrialLicense` in `LicenseUtilization.tsx`. Kept inline so
 *  the Overview-tab card stays self-contained and we don't need to lift the
 *  helper into a shared module mid-Wave-2. */
function isFreeOrTrialSku(skuPartNumber: string): boolean {
  const upper = skuPartNumber.toUpperCase()
  return FREE_LICENSE_PATTERNS.some(p => upper.includes(p))
}

/* -------------------------------------------------------------------------- */
/* Overview · People needing a hand                                           */
/* -------------------------------------------------------------------------- */

/** Severity numeral color by issueType. Per §2.3 we DO NOT tier by directory
 *  role (we don't ingest those today); we use the user's issueType as the
 *  proxy, which is editorial-honest with the data we have. */
function severityForIssue(
  issueType: string | undefined,
): 'crit' | 'high' | 'med' | 'low' {
  if (!issueType) return 'low'
  switch (issueType) {
    case 'NoMfa':
      return 'crit'
    case 'NoPhishResistantMfa':
    case 'DisabledWithLicenses':
      return 'high'
    case 'InactiveMembers':
    case 'InactiveWithLicenses':
    case 'LicenseWaste':
      return 'med'
    default:
      return 'low'
  }
}

function reasonLineForIssue(user: UserDetailItem): string {
  switch (user.issueType) {
    case 'NoMfa':
      return 'No MFA enrolled'
    case 'NoPhishResistantMfa':
      return 'Standard MFA — upgrade to phish-resistant'
    case 'DisabledWithLicenses':
      return 'Disabled · still licensed'
    case 'InactiveMembers':
    case 'InactiveWithLicenses':
      return 'Inactive · seat held'
    case 'LicenseWaste':
      return 'License waste'
    default:
      return 'Needs attention'
  }
}

/** Relative-time string from an ISO date. Matches the mockup's
 *  "Last sign-in 3d ago" style. Returns "Never" for users who have no
 *  sign-in record (not "Last sign-in N/A"). */
function relativeSignIn(iso?: string): string {
  if (!iso) return 'Never signed in'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Never signed in'
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays < 1) return 'Last sign-in today'
  if (diffDays === 1) return 'Last sign-in yesterday'
  if (diffDays < 30) return `Last sign-in ${diffDays}d ago`
  if (diffDays < 365) return `Last sign-in ${Math.floor(diffDays / 30)}mo ago`
  return `Last sign-in ${Math.floor(diffDays / 365)}y ago`
}

function initialsOf(name: string | undefined): string {
  if (!name) return '·'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function PeopleNeedingHand({
  users,
  isLoading,
}: {
  users: UserDetailItem[]
  isLoading: boolean
}) {
  return (
    <section
      data-tone="identity"
      className="rounded-3xl border border-dark-600 bg-dark-800 p-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]"
    >
      <header className="mb-3 flex flex-wrap items-end justify-between gap-3 border-b border-dark-700 pb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Section · 04
          </div>
          <h3 className="mt-1 text-[22px] font-extrabold tracking-[-0.015em] text-slate-100 dark:text-slate-100">
            People{' '}
            <em
              style={{
                fontStyle: 'italic',
                fontWeight: 600,
                color: 'var(--tone-accent-deep)',
              }}
            >
              needing a hand
            </em>
            , this week
          </h3>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-3 py-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-dark-700" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-slate-400">
          No one needs a hand right now. Identity floor is solid.
        </p>
      ) : (
        <div className="flex flex-col">
          {users.map((u, i) => (
            <PersonRow key={u.userId} user={u} rank={i + 1} />
          ))}
        </div>
      )}

      <Link
        to="/user-insights?tab=all"
        className="
          mt-5 inline-flex items-center gap-2
          border-b-[2px] pb-1 text-[11px] font-bold uppercase tracking-[0.18em]
          text-slate-100 dark:text-slate-100
        "
        style={{ borderBottomColor: 'var(--tone-accent-deep)' }}
      >
        All needing attention · open table →
      </Link>
    </section>
  )
}

const NUMERAL_COLOR_BY_SEVERITY: Record<
  ReturnType<typeof severityForIssue>,
  string
> = {
  crit: '#C84B4B',
  high: 'rgb(var(--color-secondary-sand-500))',
  med: 'var(--tone-accent-deep)',
  low: '#4A9B6E',
}

function PersonRow({ user, rank }: { user: UserDetailItem; rank: number }) {
  const severity = severityForIssue(user.issueType)
  // §2.3: NOT directory roles — jobTitle, real, or fall back to "Member".
  const role = user.jobTitle?.trim() || (user.userType === 'Guest' ? 'Guest' : 'Member')

  // Per §5: CTA is "Open user →" — NOT "Send setup link" (that's a backlog item
  // captured in posture-pages-supplements.md and the Tandm task
  // task-5AIL55HW). Deep-link into the M365 admin for now; once the row
  // drawer ships, we'll switch to that.
  const adminLink = `https://admin.microsoft.com/Adminportal/Home#/users/:/UserDetails/${encodeURIComponent(user.userId)}`

  return (
    <a
      href={adminLink}
      target="_blank"
      rel="noopener noreferrer"
      className="
        group grid grid-cols-[44px_36px_1fr_auto_auto] items-center gap-4
        border-b border-dark-700 py-3.5 transition-[padding,background] duration-200
        last:border-b-0 hover:bg-dark-700/40 hover:pl-2
      "
    >
      <span
        aria-hidden
        className="text-center font-display text-[28px] font-extrabold italic leading-none tabular-nums tracking-[-0.03em]"
        style={{ color: NUMERAL_COLOR_BY_SEVERITY[severity] }}
      >
        {pad2(rank)}
      </span>

      <span
        aria-hidden
        className="grid h-9 w-9 place-items-center rounded-full text-[12px] font-bold tracking-[-0.01em] text-[#212938]"
        style={{
          background:
            'linear-gradient(135deg, rgb(var(--color-secondary-sand-300)), rgb(var(--color-secondary-blue-300)))',
        }}
      >
        {initialsOf(user.displayName)}
      </span>

      <div className="min-w-0 leading-[1.35]">
        <div className="truncate text-[14px] font-bold text-slate-100 dark:text-slate-100">
          {user.displayName || user.userPrincipalName}
        </div>
        <div className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {role}
        </div>
      </div>

      <div className="text-right text-[12px] font-semibold leading-[1.35] text-slate-100 dark:text-slate-100">
        {reasonLineForIssue(user)}
        <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500">
          {relativeSignIn(user.lastSignInDateTime)}
        </div>
      </div>

      <span
        className="
          rounded-full border border-dark-600 bg-dark-900 px-3.5 py-1.5
          text-[10px] font-bold uppercase tracking-[0.14em] text-slate-100
          transition-colors group-hover:border-current
          dark:text-slate-100
        "
        style={{ color: 'var(--tone-accent-deep)' }}
      >
        Open user →
      </span>
    </a>
  )
}
